import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Paper,
  Stack,
  Alert,
  Progress,
  List,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { useQueryParam } from '@/hooks/useQueryParam';
import { useCreateUser } from '@/hooks/useVolunteer';
import {
  getVolunteerSession,
  hasVolunteerSession,
  clearVolunteerSession,
} from '@/lib/volunteerSession';

interface FormValues {
  password: string;
  confirmPassword: string;
}

// Password strength checker
const getPasswordStrength = (password: string): number => {
  let strength = 0;
  if (password.length >= 8) strength += 25;
  if (password.length >= 12) strength += 15;
  if (/[a-z]/.test(password)) strength += 15;
  if (/[A-Z]/.test(password)) strength += 15;
  if (/\d/.test(password)) strength += 15;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 15;
  return Math.min(strength, 100);
};

const getStrengthColor = (strength: number): string => {
  if (strength < 30) return 'red';
  if (strength < 60) return 'yellow';
  if (strength < 80) return 'blue';
  return 'green';
};

export default function CreateAccount() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const volunteerId = useQueryParam('volunteerId');
  const [error, setError] = useState<string | null>(null);

  // Use local session data instead of API
  const hasValidSession = volunteerId && hasVolunteerSession(volunteerId);
  const session = getVolunteerSession();

  const createUser = useCreateUser();

  const form = useForm<FormValues>({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validate: {
      password: value => {
        if (value.length < 8) return t('createAccount.validation.minLength');
        if (!/[a-z]/.test(value))
          return t('createAccount.validation.lowercase');
        if (!/[A-Z]/.test(value))
          return t('createAccount.validation.uppercase');
        if (!/\d/.test(value)) return t('createAccount.validation.number');
        return null;
      },
      confirmPassword: (value, values) =>
        value !== values.password
          ? t('createAccount.validation.passwordMismatch')
          : null,
    },
  });

  const passwordStrength = getPasswordStrength(form.values.password);

  const handleSubmit = async (values: FormValues) => {
    if (!volunteerId || !session?.signatureData) return;

    setError(null);

    try {
      await createUser.mutateAsync({
        application: session.application,
        signatureData: session.signatureData,
        password: values.password,
      });

      // Clear session data after successful account creation
      clearVolunteerSession();

      // Redirect to success page
      navigate('/volunteer/account-created');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t('createAccount.error.generic');
      setError(errorMessage);
    }
  };

  // Check for valid session
  if (!hasValidSession || !session) {
    return (
      <Container size="sm" py="xl">
        <Alert color="red">{t('createAccount.error.volunteerNotFound')}</Alert>
      </Container>
    );
  }

  if (!session.contractSigned || !session.signatureData) {
    return (
      <Container size="sm" py="xl">
        <Alert color="yellow">
          {t('createAccount.error.contractNotSigned')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="sm" py="xl">
      <Paper shadow="md" p="xl" radius="md" withBorder>
        <Stack gap="lg">
          <div>
            <Title order={2}>{t('createAccount.title')}</Title>
            <Text c="dimmed" mt="xs">
              {t('createAccount.subtitle', {
                name: `${session.application.firstName} ${session.application.lastName}`,
              })}
            </Text>
          </div>

          {error && (
            <Alert color="red" title={t('createAccount.error.title')}>
              {error}
            </Alert>
          )}

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <TextInput
                label={t('createAccount.email')}
                value={session.application.email}
                disabled
              />

              <div>
                <PasswordInput
                  label={t('createAccount.password')}
                  placeholder={t('createAccount.passwordPlaceholder')}
                  required
                  {...form.getInputProps('password')}
                />
                {form.values.password && (
                  <>
                    <Progress
                      value={passwordStrength}
                      color={getStrengthColor(passwordStrength)}
                      size="xs"
                      mt="xs"
                    />
                    <Text size="xs" c="dimmed" mt={4}>
                      {t('createAccount.passwordStrength')}: {passwordStrength}%
                    </Text>
                  </>
                )}
              </div>

              <PasswordInput
                label={t('createAccount.confirmPassword')}
                placeholder={t('createAccount.confirmPasswordPlaceholder')}
                required
                {...form.getInputProps('confirmPassword')}
              />

              <Text size="sm" c="dimmed">
                {t('createAccount.requirements.title')}
              </Text>
              <List size="sm" c="dimmed">
                <List.Item>
                  {t('createAccount.requirements.minLength')}
                </List.Item>
                <List.Item>
                  {t('createAccount.requirements.uppercase')}
                </List.Item>
                <List.Item>
                  {t('createAccount.requirements.lowercase')}
                </List.Item>
                <List.Item>{t('createAccount.requirements.number')}</List.Item>
              </List>

              <Button
                type="submit"
                fullWidth
                size="lg"
                loading={createUser.isPending}
              >
                {t('createAccount.submit')}
              </Button>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Container>
  );
}
